import {Box, Typography} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import AlignVerticalTopIcon from "@mui/icons-material/AlignVerticalTop";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import AlignVerticalCenterIcon from "@mui/icons-material/AlignVerticalCenter";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {RibbonButton, RibbonGroup} from "../Ribbon";
import {MenubarShortcut, useMenubarShortcuts} from "../useMenubarShortcuts";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {doorPlacementViolations, standardPosition} from "../../model/hardware/MdHardwareRules";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import {useEffect, useReducer} from "react";

type HardwareSel = Extract<MdSelection, {type: "hardware-item"}>;

// the contextual tab for a placed hardware-item crosshair. Its position is edited
// directly on the canvas (the four dimensions); the tab shows the item's identity
// and offers Remove + Manage.
export function HardwareItemSelection(props: {app: ElevationApp; selection: HardwareSel[]}) {
    const app = props.app;
    const sel = props.selection;
    // re-render when a placement's alignment changes so the buttons reflect it.
    useEventEmitter2(app.hardware.listChanged);
    // the hardware sidebar only exists on the hardware workspace, so Manage is offered there only.
    const workspace = useEventEmitter2(app.viewSettings.workspace);

    // one product may have both a door and a frame crosshair; removing works per
    // product, so dedupe.
    const productIds = Array.from(new Set(sel.map(s => s.productId)));
    const single = productIds.length === 1;
    const item = single ? (app.hardwareGroup?.hardware ?? []).find(h => h.product === productIds[0]) : undefined;

    // the actual placements behind the selection (by crosshair id), whose
    // alignment the buttons below edit.
    const placements = sel
        .map(s => app.hardware.list.find(p => p.id === s.id))
        .filter((p): p is MdHardwarePlacement => !!p);

    // re-render as the selected placements move (drag / dimension edit) so the rule
    // check below tracks them.
    const [, forceRender] = useReducer((x: number) => x + 1, 0);
    const placementKey = placements.map(p => p.id).join(",");
    useEffect(() => {
        const subs = placements.flatMap(p => [p.x.subscribe(() => forceRender()), p.y.subscribe(() => forceRender())]);
        return () => subs.forEach(sub => sub.cancel());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placementKey]);

    const violations = placements.flatMap(p => {
        const hw = (app.hardwareGroup?.hardware ?? []).find(h => h.product === p.productId);
        return hw ? doorPlacementViolations(app, p, hw, app.draw.formatDimension) : [];
    });

    const alignX = placements[0]?.alignToX ?? "left";
    const alignY = placements[0]?.alignToY ?? "top";

    const setAlignX = (v: MdHardwarePlacement["alignToX"]) => {
        for(const p of placements) p.alignToX = v;
        app.hardware.listChanged.emit("align");
    };
    const setAlignY = (v: MdHardwarePlacement["alignToY"]) => {
        for(const p of placements) p.alignToY = v;
        app.hardware.listChanged.emit("align");
    };

    const xOptions = [
        {value: "left", label: "Left", icon: <AlignHorizontalLeftIcon />},
        {value: "right", label: "Right", icon: <AlignHorizontalRightIcon />},
        {value: "both", label: "Both", icon: <AlignHorizontalCenterIcon />},
    ] as const;
    const yOptions = [
        {value: "top", label: "Top", icon: <AlignVerticalTopIcon />},
        {value: "bottom", label: "Bottom", icon: <AlignVerticalBottomIcon />},
        {value: "both", label: "Both", icon: <AlignVerticalCenterIcon />},
    ] as const;

    const remove = () => {
        for(const id of productIds) app.hardware.remove(id);
        // drop the now-stale tab and fall back to the workspace tab.
        app.viewSettings.selection.emit([]);
        app.viewSettings.activeTab.emit("workspace");
    };

    // put each selected placement back at its standard location: a door placement at its
    // category / kind standard (see standardPosition); a frame placement recentres on its
    // stick (the crosshair auto-centres while unmodified).
    const reset = () => {
        const hw = app.hardwareGroup?.hardware ?? [];
        for(const p of placements) {
            const product = hw.find(h => h.product === p.productId);
            if(p.target === "door" && product) {
                const pos = standardPosition(app, p, product);
                p.x.userSet(pos.x, {debug: `${p.id}.reset`});
                p.y.userSet(pos.y, {debug: `${p.id}.reset`});
            } else {
                p.x.userClearModified();
                p.y.userClearModified();
            }
        }
        app.hardware.listChanged.emit("reset");
    };

    // link / unlink the selected placements with their rule partners (an exit trim and its
    // exit device). Unlink is offered while any selected placement is linked; Link while
    // none is and one has a partner to line up with.
    const linked = placements.filter(p => app.hardware.partnerOf(p));
    const linkable = placements
        .map(p => ({p, partner: app.hardware.linkCandidate(p)}))
        .filter((e): e is {p: MdHardwarePlacement; partner: MdHardwarePlacement} => !!e.partner);
    const unlink = () => {
        for(const p of linked) app.hardware.unlink(p);
    };
    const link = () => {
        for(const e of linkable) app.hardware.link(e.p, e.partner);
    };

    const removeShortcut: MenubarShortcut = {
        keys: ["Delete", "Backspace"], display: "Delete",
        hint: single ? "Press Delete to remove the hardware" : "Press Delete to remove the hardware",
        run: remove,
    };
    useMenubarShortcuts([removeShortcut]);

    return (
        <>
            <RibbonGroup label="Item">
                <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", px: 1, minWidth: 120}}>
                    {item ? (
                        <>
                            <Typography sx={{pt: 1, fontSize: 13, fontWeight: 600, lineHeight: 1.3}}>{item.productCode}</Typography>
                            <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.3}}>{item.category}</Typography>
                            <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.3}}>{item.manufacturerName}</Typography>
                        </>
                    ) : (
                        <Typography sx={{fontSize: 12, color: "text.secondary"}}>
                            {productIds.length} items
                        </Typography>
                    )}
                </Box>
            </RibbonGroup>

            {violations.length > 0 && (
                <RibbonGroup label="Placement">
                    <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", px: 1, maxWidth: 320}}>
                        {violations.map((v, i) => (
                            <Typography key={i} sx={{fontSize: 11, color: "error.main", lineHeight: 1.3}}>
                                {v.category}: {v.message}
                            </Typography>
                        ))}
                    </Box>
                </RibbonGroup>
            )}

            {placements.length > 0 && (
                <RibbonGroup label="Horizontal">
                    {xOptions.map(o => (
                        <RibbonButton
                            key={o.value}
                            icon={o.icon}
                            label={o.label}
                            active={alignX === o.value}
                            onClick={() => setAlignX(o.value)}
                        />
                    ))}
                </RibbonGroup>
            )}

            {placements.length > 0 && (
                <RibbonGroup label="Vertical">
                    {yOptions.map(o => (
                        <RibbonButton
                            key={o.value}
                            icon={o.icon}
                            label={o.label}
                            active={alignY === o.value}
                            onClick={() => setAlignY(o.value)}
                        />
                    ))}
                </RibbonGroup>
            )}

            <RibbonGroup label="Actions">
                <RibbonButton
                    icon={<RestartAltIcon />}
                    label="Reset"
                    onClick={reset}
                />
                {linked.length > 0 && (
                    <RibbonButton
                        icon={<LinkOffIcon />}
                        label="Unlink"
                        onClick={unlink}
                    />
                )}
                {linked.length === 0 && linkable.length > 0 && (
                    <RibbonButton
                        icon={<LinkIcon />}
                        label="Link"
                        onClick={link}
                    />
                )}
                <RibbonButton
                    icon={<DeleteOutlineIcon sx={{color: "error.main"}} />}
                    label="Remove"
                    shortcut={removeShortcut.display}
                    onClick={removeShortcut.run}
                />
                {workspace === "hardware" && (
                    <RibbonButton
                        icon={<TuneIcon />}
                        label="Manage"
                        onClick={() => app.viewSettings.hardwareSidebarCollapsed.emit(false)}
                    />
                )}
            </RibbonGroup>
        </>
    );
}
